import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextArea } from 'view/atoms';
import { translateError } from 'utils/translateError';
import { PopupConfirm } from 'view/molecules/PopupConfirm';
import {
  COMMENT_MAX_LENGTH,
  schema,
} from './DiscountStatusCommentModal.schema';
import * as S from './DiscountStatusCommentModal.styles';

type Props = {
  placeholder: string;
  closeHandler: () => void;
  confirmHandler: (comment: string) => void;
  title?: string;
  description?: string;
  confirmText?: string;
  isConfirmDisabled?: boolean;
  'data-testid'?: string;
  confirmButtonTestId?: string;
  commentInputTestId?: string;
};

export const DiscountStatusCommentModal = ({
  placeholder,
  closeHandler,
  confirmHandler,
  title,
  description,
  confirmText,
  isConfirmDisabled,
  'data-testid': dataTestId,
  confirmButtonTestId = 'discount-status-comment-confirm-button',
  commentInputTestId = 'discount-status-comment-input',
}: Props) => {
  const { t } = useTranslation();

  const {
    register,
    formState: {
      errors: { comment: commentError },
    },
    watch,
    setValue,
  } = useForm<{ comment: string }>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const comment = watch('comment');
  const isDisabledConfirm =
    !!isConfirmDisabled || !!commentError || !comment?.trim();

  const handleConfirm = () => {
    if (isDisabledConfirm) {
      return;
    }

    confirmHandler(comment.trim());
  };

  return (
    <PopupConfirm
      title={title ?? t('archives.statusCommentModal.title')}
      description={description}
      yesText={confirmText ?? t('other.submit')}
      cancelText={t('other.cancel')}
      confirmHandler={handleConfirm}
      closeHandler={closeHandler}
      isDisabledConfirm={isDisabledConfirm}
      areButtonsHorizontal
      data-testid={dataTestId}
      confirmButtonTestId={confirmButtonTestId}
    >
      <S.TextAreaWrapper>
        <TextArea
          placeholder={placeholder}
          {...register('comment', {
            onBlur: (e) => setValue('comment', e.target.value.trim()),
          })}
          error={translateError(commentError, t)}
          hasCounter
          maxLength={COMMENT_MAX_LENGTH}
          value={comment}
          data-testid={commentInputTestId}
        />
      </S.TextAreaWrapper>
    </PopupConfirm>
  );
};
